import React from 'react'

import {
  AnalysisCommentBlock,
  useGenerateSingleComment,
} from '~/components/features/AnalysisCommentBlock'
import { DisplayToggleButton } from '~/components/features/DisplayToggleButton'
import { ShareDropDownMenuButton } from '~/components/features/ShareDropDownMenuButton'
import { SortButton } from '~/components/features/SortButton'
import { Table } from '~/components/features/Table'
import { TableContainer } from '~/components/features/TableContainer'
import { useLogEvent } from '~/hooks/useLogEvent'
import { exists } from '~/utils'

import { useExpandText } from '../hooks/useExpandText'
import { useGroupSortItems } from '../hooks/useGroupSortItems'
import { useSequentialDisplayRowItems } from '../hooks/useSequentialDisplayRowItems'
import { useSequentialTableColumns } from '../hooks/useSequentialTableColumns'
import { useSequentialTableInfo } from '../hooks/useSequentialTableInfo'
import { useSerializeGroupTable } from '../hooks/useSerializeGroupTable'
import { useTabulatedGroups } from '../hooks/useTabulatedGroups'
import {
  useAtomContext,
  useCommentContext,
  useSavedCommentContext,
  useSetAtomContext,
} from '../providers/AtomsProvider'
import { useCommentGenerationContext } from '../providers/CommentGenerationProvider'
import { useFigureInfo } from '../providers/CreateFigureInfoProvider'
import { useRestoringParams } from '../providers/CreateRestoringParamsProvider'

import { EditGroupsDiffButton } from './EditGroupsDiffButton'
import styles from './SequentialTable.css'

export const SequentialTable = () => {
  const sortButtonItems = useGroupSortItems()

  const [sortValue, setSortValue] = useAtomContext('sequentialSortValue')

  const displayRowItems = useSequentialDisplayRowItems()
  const setHiddenRowIds = useSetAtomContext('hiddenRowIds')

  const columns = useSequentialTableColumns()

  const figureInfo = useFigureInfo('continuous_questionnaire')

  const restoringParams = useRestoringParams('sequential')

  const tableInfo = useSequentialTableInfo({
    answerText: styles.cellAnswerTextColumn,
    questionText: styles.cellQuestionTextColumn,
  })

  const { handleTextExpandedChange, isTextExpanded, textLineCount } =
    useExpandText(tableInfo)

  const image = (
    <Table
      columns={columns}
      tableInfo={tableInfo}
      textLineCount={textLineCount}
    />
  )

  const tabulatedGroups = useTabulatedGroups()

  const { generate, isGenerating } = useGenerateSingleComment()
  const { outputType, surveyId } = useCommentGenerationContext()
  const serializeGroupTable = useSerializeGroupTable()

  const [comment, setComment] = useCommentContext('sequential')
  const [savedComment, setSavedComment] = useSavedCommentContext('sequential')

  const logEvent = useLogEvent()
  const handleAutoGenerate = React.useCallback(async () => {
    const result = await generate({
      brandName: '',
      filterDescription: '',
      filterPatternIndex: 0,
      outputType,
      segmentMode: 'none',
      surveyId,
      tabulationData: serializeGroupTable(columns, tableInfo),
    })
    if (exists(result)) {
      setComment(result)
      logEvent('Generate AI comment', { withSuffix: true })
    }
  }, [
    generate,
    logEvent,
    outputType,
    serializeGroupTable,
    surveyId,
    columns,
    tableInfo,
    setComment,
  ])

  return (
    <>
      <AnalysisCommentBlock
        isGenerating={isGenerating}
        savedComment={savedComment}
        saveDocumentImage={{
          commentReadOnly: true,
          figureInfo,
          image,
          initialComment: comment,
          onSaved: (saved) => setSavedComment(saved),
          restoringParams,
        }}
        value={comment}
        onAutoGenerate={handleAutoGenerate}
        onCommentChange={setComment}
      />
      <TableContainer
        className={styles.root}
        header={
          <>
            <SortButton
              items={sortButtonItems}
              value={sortValue}
              onChange={setSortValue}
            />

            <DisplayToggleButton
              isTextExpanded={isTextExpanded}
              isVisibleExpandTextCheckbox={true}
              rowItems={displayRowItems}
              onHiddenRowChange={setHiddenRowIds}
              onTextExpandedChange={handleTextExpandedChange}
            />

            <ShareDropDownMenuButton
              copyToClipboard={{
                columns,
                figureInfo,
                restoringParams,
                tableInfo,
              }}
              downloadAsPng={{ image }}
              getSharedUrl={{ restoringParams }}
              saveDocumentImage={{
                commentReadOnly: true,
                figureInfo,
                image,
                initialComment: comment,
                onSaved: (saved) => setSavedComment(saved),
                restoringParams,
              }}
            />

            {1 <= tabulatedGroups.length && <EditGroupsDiffButton />}
          </>
        }
        id="sequential"
      >
        <Table
          columns={columns}
          stickyColumns={2}
          tableInfo={tableInfo}
          textLineCount={textLineCount}
        />
      </TableContainer>
    </>
  )
}
